import { Trans, useLingui } from '@lingui/react/macro';

import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FilterSearch } from '@/design_system/Filters/FiltersMenu/FilterSearch';
import {
  FilterItemNoResults,
  FiltersMenu,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';
import IconTask from '@/icons/Task.svg';

import { useFilters } from '../FiltersState';
import { useRoleFilterState } from '../shared';

const MINIMUM_ROLES_FOR_SEARCH = 5;

export const FiltersRole = ({ tag }: { tag?: boolean }) => {
  const { t } = useLingui();

  const {
    roles: { roles, setRoles },
  } = useFilters();

  const {
    search,
    setSearch,
    fetchNextPage,
    isLoading,
    roles: roleOptions,
    count,
  } = useRoleFilterState();

  const options = roleOptions.map(({ id, name }) => (
    <FilterCheckboxItem
      key={id}
      checked={roles.some((role) => role.id === id)}
      onChange={(checked) => {
        if (checked) {
          setRoles([...roles, { id, name }]);
        } else {
          setRoles(roles.filter((role) => role.id !== id));
        }
      }}
    >
      {name}
    </FilterCheckboxItem>
  ));

  if (options.length === 0) {
    options.push(<FilterItemNoResults key="empty-desktop" />);
  }

  const searchWithOptions = (
    <FilterSearch
      inputProps={{
        ariaLabel: t({
          id: 'settings.organization.members.invitations.filters.role.search.label',
          message: 'Search roles',
        }),
        placeholder: t({
          id: 'settings.organization.members.invitations.filters.role.search.placeholder',
          message: 'Search roles...',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={roleOptions.length === 0 && isLoading}
      isLoadingMore={isLoading}
    >
      {options}
    </FilterSearch>
  );

  const children = count < MINIMUM_ROLES_FOR_SEARCH ? options : searchWithOptions;

  if (tag) {
    if (roles.length === 0) {
      return null;
    }

    const numberOfRoles = roles.length;

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={t({
              id: 'settings.organization.members.invitations.filters.role.label',
              message: 'Role',
            })}
            value={
              roles.length === 1 ? (
                roles[0].name
              ) : (
                <Trans id="settings.organization.members.invitations.filters.role.multiple">
                  {numberOfRoles} roles
                </Trans>
              )
            }
            tooltip={roles.length > 1 ? roles.map((role) => role.name).join(', ') : undefined}
            onClear={() => setRoles([])}
          />
        }
      >
        {children}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu
      icon={<IconTask />}
      label={t({
        id: 'settings.organization.members.invitations.filters.role.label',
        message: 'Role',
      })}
    >
      {children}
    </FiltersSubMenu>
  );
};
